import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { Box, Link as DSLink, Typography } from '@saviynt/design-system';

import { RiskGraph, SodReloader } from '../../../../shared/components';
import {
  CF_NEU_STL,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type ActiveGraphPointState } from '../../../../shared/types';
import { useGetViolationsGraphMutation } from '../../data-layer/violationsTabApi';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { getFilterPayload, getPoinSize } from '../../utils';

import './ViolationsTabGraph.css';

const FILTER_VALUE_TO_TEXT_MAP: Record<string, string> = {
  LOW: 'LOW',
  MEDIUM: 'MODERATE',
  MODERATE: 'MODERATE',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

const FILTER_TEXT_TO_VALUE_MAP: Record<string, string> = {
  LOW: 'LOW',
  MODERATE: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

interface PlotDataState {
  maxViolationAge: number;
  maxViolationScore: number;
  minViolationAge: number;
  minViolationScore: number;
  totalViolations: number;
  timeRange: string;
  updatedTime: string;
  clusters: ActiveGraphPointState[];
}

/**
 * ViolationsTabGraph
 */
function ViolationsTabGraph(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const {
    activeFilters: mainActiveFilters,
    tabInAction,
    searchKey,
    activeInsight,
    activeGraphPoint,
  } = useTypedSelector((state) => state.aag.violationsTab.controls);

  const { activeFilters: entitlementActiveFilters } = useTypedSelector(
    (state) => state.aag.details.entitlementViolationsTab
  );

  const { selectedEntitlement } = useTypedSelector(
    (state) => state.aag.details.entitlementSummary
  );

  const isEntitlementContext = useMemo(
    () => window.location.pathname.includes('entitlement'),
    []
  );
  const activeFilters = useMemo(() => {
    if (isEntitlementContext) {
      return entitlementActiveFilters;
    }

    return mainActiveFilters;
  }, [entitlementActiveFilters, isEntitlementContext, mainActiveFilters]);

  const [isFirstTime, setIsFirstTime] = useState(false);
  const [isSectorClicked, setIsSectorClicked] = useState(false);
  const [data, setData] = useState<PlotDataState | null>({
    clusters: [] as ActiveGraphPointState[],
  } as PlotDataState);
  const isFilteringFromSectorRef = useRef(false);

  const abortRef = useRef<((reason: string) => void) | null>(null);
  const [fetchGraphPoints, { isError, isLoading }] =
    useGetViolationsGraphMutation();

  const handleFetchGraphPoints = useCallback(async () => {
    abortRef.current?.('New Search');

    const activeFiltersMap = Object.fromEntries(
      activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
    );
    const baseFilters = getFilterPayload(
      activeFiltersMap,
      tabInAction ?? undefined,
      searchKey
    );

    const filters = {
      ...baseFilters,
      insightRequests:
        activeInsight !== null
          ? [
              {
                id: activeInsight.id,
                filters: activeInsight.filters,
              },
            ]
          : undefined,
    };

    if (
      isEntitlementContext &&
      selectedEntitlement?.entitlementValue &&
      selectedEntitlement.riskObjects.length
    ) {
      filters.entitlement = {
        ...filters.entitlement,
        value: [selectedEntitlement.entitlementValue],
      };
    }

    try {
      const controller = fetchGraphPoints({
        filters,
      });

      abortRef.current = () => controller.abort();

      const response = await controller.unwrap();

      return response;
    } catch {
      throw Error('Failed graph API');
    }
  }, [
    activeFilters,
    tabInAction,
    searchKey,
    activeInsight,
    fetchGraphPoints,
    isEntitlementContext,
    selectedEntitlement,
  ]);

  const triggerFetchGraphPoints = useCallback(() => {
    handleFetchGraphPoints()
      .then((response) => {
        const typedResponse = response as PlotDataState | null;

        if (typedResponse !== null) {
          setData(typedResponse);
          setIsFirstTime(true);

          return;
        }

        setData(null);
        setIsFirstTime(false);
      })
      .catch(() => {
        Logger.log('Error in graph');

        setIsFirstTime(false);
        dispatch(violationsTabActions.setGraphRowContext(null));
        dispatch(violationsTabActions.setActiveGraphPoint(null));
      });
  }, [dispatch, handleFetchGraphPoints]);

  useEffect(() => {
    triggerFetchGraphPoints();
  }, [triggerFetchGraphPoints]);

  useEffect(() => {
    if (!isFilteringFromSectorRef.current) {
      setIsSectorClicked(false);
    }

    isFilteringFromSectorRef.current = false;
  }, [activeFilters]);

  // Constants
  const severityMap = useMemo(() => {
    const severityOrder = [
      // 'LOW',
      // 'MEDIUM',
      'MODERATE',
      'HIGH',
      'CRITICAL',
    ];

    const countBySeverity = severityOrder.reduce<Record<string, number>>(
      (acc, severity) => {
        if (data === null) {
          return acc;
        }

        const total = data.clusters
          .filter((item) => item.severity === severity)
          .reduce((sum, item) => sum + item.count, 0);

        if (isSectorClicked) {
          if (total > 0) {
            acc[severity] = total;
          }
        } else {
          acc[severity] = total;
        }

        return acc;
      },
      {}
    );

    return countBySeverity;
  }, [data, isSectorClicked]);

  const chartScales = useMemo(() => {
    if (!data) {
      return {
        x_min: 0,
        x_max: 0,
        y_min: 25,
        y_max: 100,
      };
    }

    if (
      data.clusters.length === 1 ||
      data.minViolationAge === data.maxViolationAge
    ) {
      return {
        x_min: Math.max(data.minViolationAge - 10, 0),
        x_max: data.maxViolationAge + 10,
        y_min: 25,
        y_max: 100,
      };
    }

    return {
      x_min: data.minViolationAge,
      x_max: data.maxViolationAge,
      y_min: 25,
      y_max: 100,
    };
  }, [data]);

  const secondaryContent = useMemo(() => {
    if (activeFilters.length > 0) {
      const filterCount = isEntitlementContext
        ? activeFilters.filter((filter) => filter.value !== 'riskKeys').length
        : activeFilters.length;

      if (filterCount > 0) {
        return (
          <Box
            tag='div'
            className='Sod-ViolationsTabGraph-riskGraphSecondaryContent'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('{count} filter applied', { count: filterCount })}
            </Typography>
            <DSLink
              text={l('Clear')}
              kind='button'
              onClick={() => {
                // dispatch(violationsTabActions.setIsFilterBarOpen(true));
                dispatch(violationsTabActions.setActiveFilters([]));
              }}
              size='small'
            />
          </Box>
        );
      }
    }

    if (activeInsight) {
      return (
        <Box
          tag='div'
          className='Sod-ViolationsTabGraph-riskGraphSecondaryContent'>
          <Typography kind='body3' color={CF_NEU_STL}>
            {l('Insight applied')}
          </Typography>
          <DSLink
            text={l('Clear')}
            kind='button'
            onClick={() => {
              dispatch(violationsTabActions.setActiveInsight(null));
            }}
            size='small'
          />
        </Box>
      );
    }

    return null;
  }, [l, dispatch, activeFilters, activeInsight, isEntitlementContext]);

  const isSectorInteractive = useMemo(() => {
    if (data === null || data.clusters.length <= 2 || isSectorClicked) {
      return false;
    }

    return true;
  }, [data, isSectorClicked]);

  const renderBody = useCallback(() => {
    if (isError || data === null) {
      return <SodReloader triggerRefresh={() => triggerFetchGraphPoints()} />;
    }

    if (isFirstTime) {
      return (
        <Box className='Sod-ViolationsTabGraph-riskGraphHolder'>
          <RiskGraph
            isAnimation
            isFilterActive
            isProcessing={isLoading}
            isSectorInteractive={isSectorInteractive}
            plotPoints={data.clusters.map((point) => ({
              id: `${tabInAction ?? ''}--||--${point.clusterId}`,
              xIn: point.violationAge,
              yIn: point.violationScore,
              size: getPoinSize(point.count),
              isActive: activeGraphPoint?.clusterId === point.clusterId,
              category: point.severity,
              tooltipData: {
                count: point.count,
                severity: point.severity,
                violationAgeRange: point.violationAgeRange,
                scoreRange: point.scoreRange,
                violationScore: point.violationScore,
              },
            }))}
            centerCount={data.totalViolations}
            chartScales={chartScales}
            onPointClick={(pointId, isAlreadyActive) => {
              if (isAlreadyActive) {
                dispatch(violationsTabActions.setIsInsightsOpen(true));

                return;
              }

              const targetClusterId = (pointId.id as string).split('--||--')[1];

              const targetCluster = data.clusters.find(
                (cluster) => cluster.clusterId === targetClusterId
              );

              batch(() => {
                dispatch(
                  violationsTabActions.setActiveGraphPoint(
                    targetCluster ?? null
                  )
                );
                dispatch(violationsTabActions.setGraphRowContext(null));
                dispatch(violationsTabActions.setIsInsightsOpen(true));
              });
            }}
            onSectorClick={(sector) => {
              const typedSector = sector as {
                xLow: number;
                xHigh: number;
                yLow: number;
                yHigh: number;
              };

              const newFilters = [
                ...activeFilters.filter(
                  (f) =>
                    f.value !== VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.value &&
                    f.value !== VIOLATIONS_TAB_LIST_AD_FILTER.VIOLATIONAGE.value
                ),
                {
                  value: VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.value,
                  method: VIOLATIONS_TAB_LIST_AD_FILTER.RISKSCORE.method,
                  options: [
                    {
                      label: '',
                      value: `[${Math.floor(typedSector.yLow)},${Math.floor(
                        typedSector.yHigh
                      )}]`,
                      isChecked: true,
                    },
                  ],
                },
                {
                  value: VIOLATIONS_TAB_LIST_AD_FILTER.VIOLATIONAGE.value,
                  method: VIOLATIONS_TAB_LIST_AD_FILTER.VIOLATIONAGE.method,
                  options: [
                    {
                      label: '',
                      value: `[${Math.floor(typedSector.xLow)},${Math.floor(
                        typedSector.xHigh
                      )}]`,
                      isChecked: true,
                    },
                  ],
                },
              ];

              isFilteringFromSectorRef.current = true;

              dispatch(violationsTabActions.setActiveFilters(newFilters));

              setIsSectorClicked(true);
            }}
            filterCategories={Object.keys(severityMap)
              .reverse()
              .map((fv) => {
                const mappedValue = FILTER_VALUE_TO_TEXT_MAP[fv] as
                  | string
                  | undefined;

                if (mappedValue) {
                  return mappedValue;
                }

                return fv;
              })}
            filterTooltips={Object.keys(severityMap)
              .map((severity) => {
                const mappedSeverity =
                  (FILTER_VALUE_TO_TEXT_MAP[severity] as string | undefined) ??
                  severity;
                const count = severityMap[severity];

                return l(
                  `{count} ${
                    String(mappedSeverity).charAt(0).toUpperCase() +
                    String(mappedSeverity).slice(1).toLowerCase()
                  } Violations`,
                  { count }
                );
              })
              .reverse()}
            handleFilterClick={(filterValue) => {
              const mappedValue =
                (FILTER_TEXT_TO_VALUE_MAP[filterValue] as string | undefined) ??
                filterValue;

              const newFilters = [
                ...activeFilters.filter(
                  (f) =>
                    f.value !== VIOLATIONS_TAB_LIST_AD_FILTER.PRIORITY.value
                ),
                {
                  value: VIOLATIONS_TAB_LIST_AD_FILTER.PRIORITY.value,
                  method: VIOLATIONS_TAB_LIST_AD_FILTER.PRIORITY.method,
                  options:
                    VIOLATIONS_TAB_LIST_AD_FILTER.PRIORITY.options
                      ?.filter((o) => o.value === mappedValue)
                      .map((o) => ({
                        ...o,
                        isChecked: true,
                      })) ?? [],
                },
              ];

              dispatch(violationsTabActions.setActiveFilters(newFilters));
            }}
            secondaryContent={secondaryContent}
            xAxisTitle={l('Violation Age (Day)')}
            yAxisTitle={l('Risk Score')}
          />
        </Box>
      );
    }

    return null;
  }, [
    l,
    dispatch,
    isError,
    isLoading,
    data,
    isFirstTime,
    isSectorInteractive,
    severityMap,
    activeFilters,
    tabInAction,
    chartScales,
    activeGraphPoint,
    secondaryContent,
    triggerFetchGraphPoints,
  ]);

  return <Box className='Sod-ViolationsTabGraph'>{renderBody()}</Box>;
}

export default memo(ViolationsTabGraph);
